import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';
import {
  AllocationBucketTypes,
  AllocationDurationTypes,
  AllocationTypes,
  AllocationStatuses,
  type AllocationBucketType,
  type AllocationDurationType,
  type AllocationType,
  type AllocationStatus,
} from './allocation.types';

const timestampSchema = z
  .instanceof(Timestamp)
  .or(z.date().transform((value) => Timestamp.fromDate(value)));

const AllocationBaseSchema = z.object({
  allocationType: z.nativeEnum(AllocationTypes),
  durationType: z.nativeEnum(AllocationDurationTypes),
  bucketType: z.nativeEnum(AllocationBucketTypes),
  bucket: z
    .number({
      required_error: 'O número de horas é obrigatório',
      invalid_type_error: 'O número de horas deve ser um número',
    })
    .int()
    .min(0, 'O número de horas deve ser positivo'),
  startDate: timestampSchema,
  endDate: timestampSchema.nullable().optional(),
  clientId: z.string().uuid().nullable().optional(),
  clientName: z.string().nullable().optional(),
  clientLogo: z.string().nullable().optional(),
  clientResponsible: z.string().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  projectName: z.string().nullable().optional(),
  capacityId: z.string().nullable().optional(),
  capacityLabel: z.string().nullable().optional(),
  capacityTypeId: z.string().nullable().optional(),
  capacityTypeName: z.string().nullable().optional(),
  capacityTechnologyId: z.string().nullable().optional(),
  capacityTechnologyName: z.string().nullable().optional(),
  capacitySeniorityId: z.string().nullable().optional(),
  capacitySeniorityName: z.string().nullable().optional(),
  rate: z.number().nullable().optional(),
  createdById: z.string().min(1, 'ID do criador é obrigatório'),
  createdByName: z.string().min(1, 'Nome do criador é obrigatório'),
  createdByAvatar: z.string().nullable().optional(),
  updatedById: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
  updatedByAvatar: z.string().nullable().optional(),
  status: z.nativeEnum(AllocationStatuses),
  cancelReason: z.string().nullable().optional(),
  canceledAt: timestampSchema.nullable().optional(),
  canceledById: z.string().nullable().optional(),
  canceledByName: z.string().nullable().optional(),
  canceledByAvatar: z.string().nullable().optional(),
  noticeReason: z.string().nullable().optional(),
  terminationAppliedAt: timestampSchema.nullable().optional(),
});

export const AllocationCreateSchema = AllocationBaseSchema.extend({
  startDate: timestampSchema,
}).refine(
  (data) => {
    if (data.durationType === AllocationDurationTypes.Defined) {
      return Boolean(data.endDate);
    }
    return true;
  },
  {
    message: 'Data de fim é obrigatória quando a duração é definida',
    path: ['endDate'],
  }
);

export const AllocationUpdateSchema = AllocationBaseSchema.partial()
  .extend({
    id: z.string().min(1, 'O ID da alocação é obrigatório'),
  })
  .refine(
    (data) => {
      if (
        data.durationType === AllocationDurationTypes.Defined &&
        data.endDate === null
      ) {
        return false;
      }
      return true;
    },
    {
      message: 'Data de fim é obrigatória quando a duração é definida',
      path: ['endDate'],
    }
  );

export type AllocationSchema = z.infer<typeof AllocationBaseSchema>;
export type AllocationCreateInput = z.infer<typeof AllocationCreateSchema>;
export type AllocationUpdateInput = z.infer<typeof AllocationUpdateSchema>;
